/**
 * What the app logged recently, kept so the user can send it to MedicAI Support when they ask for it (a log request in
 * the support chat). A page cannot read the browser's own DevTools console, so this keeps its own copy from the moment
 * the app starts: console output, uncaught errors and unhandled promise rejections, and failed HTTP calls (method, URL
 * without its query string, status, time taken — never headers or bodies).
 *
 * Kept in memory only, the latest MAX_ENTRIES, and never sent anywhere without the user's say-so (the support chat's
 * prompt). Installed once, at the very top of main.ts, so errors during start-up are caught too. Framework-free.
 */

type Level = 'log' | 'info' | 'warn' | 'error' | 'debug' | 'uncaught' | 'rejection' | 'http';

interface Entry {
  at: number;
  level: Level;
  text: string;
}

const MAX_ENTRIES = 1000;
const MAX_TEXT = 4000;
const CONSOLE_LEVELS = ['log', 'info', 'warn', 'error', 'debug'] as const;

const entries: Entry[] = [];
let installed = false;
let startedAt = 0;

function record(level: Level, text: string): void {
  // The hub refuses a text file with a NUL in it.
  const clean = text.replace(/\u0000/g, '');
  entries.push({ at: Date.now(), level, text: clean.length > MAX_TEXT ? `${clean.slice(0, MAX_TEXT)}… (cut)` : clean });
  if (entries.length > MAX_ENTRIES) entries.splice(0, entries.length - MAX_ENTRIES);
}

/** One console argument as text: errors with their stack, objects as JSON (cycles and DOM nodes named, not walked). */
function describe(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.stack && value.stack.includes(value.message) ? value.stack : `${value.name}: ${value.message}${value.stack ? `\n${value.stack}` : ''}`;
  if (value === undefined || value === null || typeof value !== 'object') return String(value);
  if (typeof Element !== 'undefined' && value instanceof Element) return `<${value.tagName.toLowerCase()}${value.id ? `#${value.id}` : ''}>`;
  const seen = new WeakSet<object>();
  try {
    return JSON.stringify(value, (_key, v: unknown) => {
      if (v instanceof Error) return { name: v.name, message: v.message, stack: v.stack };
      if (typeof v === 'function') return `[function ${v.name || 'anonymous'}]`;
      if (typeof Element !== 'undefined' && v instanceof Element) return `<${v.tagName.toLowerCase()}>`;
      if (v && typeof v === 'object') {
        if (seen.has(v)) return '[circular]';
        seen.add(v);
      }
      return v;
    }) ?? String(value);
  } catch {
    return Object.prototype.toString.call(value);
  }
}

/**
 * A URL as it goes in the logs: no query string or fragment (they carry search terms and tokens), and any long
 * token-like path segment (an upload ticket, a signed link) replaced. Ids such as GUIDs stay: they are what support
 * needs to find the record.
 */
function safeUrl(raw: string): string {
  try {
    const u = new URL(raw, location.href);
    const path = u.pathname.split('/').map((s) => (s.length >= 40 && /^[A-Za-z0-9._~%-]+$/.test(s) ? '[token]' : s)).join('/');
    return `${u.origin}${path}`;
  } catch {
    return raw.split(/[?#]/)[0] ?? '';
  }
}

function recordHttp(method: string, url: string, status: number, started: number, error?: unknown): void {
  const ms = Math.round(performance.now() - started);
  record('http', `${method.toUpperCase()} ${safeUrl(url)} -> ${status || 'network error'} (${ms} ms)${error ? ` ${describe(error)}` : ''}`);
}

function patchFetch(): void {
  if (typeof fetch !== 'function') return;
  const original = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const started = performance.now();
    const method = init?.method ?? (input instanceof Request ? input.method : 'GET');
    const url = input instanceof Request ? input.url : String(input);
    try {
      const res = await original(input, init);
      if (!res.ok) recordHttp(method, url, res.status, started);
      return res;
    } catch (e) {
      // An aborted request was cancelled on purpose: not a failure.
      if (!(e instanceof DOMException && e.name === 'AbortError')) recordHttp(method, url, 0, started, e);
      throw e;
    }
  };
}

/** Angular's HttpClient uses XMLHttpRequest, so this is where most API failures show up. */
function patchXhr(): void {
  if (typeof XMLHttpRequest === 'undefined') return;
  const proto = XMLHttpRequest.prototype;
  const open = proto.open;
  const send = proto.send;
  const calls = new WeakMap<XMLHttpRequest, { method: string; url: string; started: number; aborted: boolean }>();
  proto.open = function (this: XMLHttpRequest, method: string, url: string | URL, ...rest: unknown[]) {
    calls.set(this, { method, url: String(url), started: 0, aborted: false });
    return (open as (...a: unknown[]) => void).call(this, method, url, ...rest);
  } as typeof proto.open;
  proto.send = function (this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
    const call = calls.get(this);
    if (call) {
      call.started = performance.now();
      this.addEventListener('abort', () => (call.aborted = true));
      this.addEventListener('loadend', () => {
        if (!call.aborted && (this.status === 0 || this.status >= 400)) recordHttp(call.method, call.url, this.status, call.started);
      });
    }
    return send.call(this, body);
  };
}

/** Starts keeping the app's logs. Safe to call more than once, and does nothing outside a browser (SSR). */
export function installLogCapture(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  startedAt = Date.now();
  for (const level of CONSOLE_LEVELS) {
    const original = console[level];
    console[level] = (...args: unknown[]) => {
      try {
        record(level, args.map(describe).join(' '));
      } catch {
        // keeping a copy must never break logging itself
      }
      original.apply(console, args);
    };
  }
  window.addEventListener('error', (e: ErrorEvent) => record('uncaught', e.error ? describe(e.error) : `${e.message} at ${e.filename}:${e.lineno}:${e.colno}`));
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => record('rejection', describe(e.reason)));
  patchFetch();
  patchXhr();
}

/**
 * The logs as a text file, oldest first, under a header describing the device and app (`about`: app version, platform,
 * the page, ...). What the user sends when they accept a log request.
 */
export function logReport(about: Record<string, string | number | boolean | null | undefined>): string {
  const iso = (t: number) => new Date(t).toISOString();
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  const header: Record<string, unknown> = {
    'Captured at': iso(Date.now()),
    'Recording since': startedAt ? iso(startedAt) : 'not started',
    ...about,
    'User agent': nav?.userAgent,
    Language: nav?.language,
    'Time zone': Intl.DateTimeFormat().resolvedOptions().timeZone,
    Online: nav?.onLine,
    Screen: typeof screen !== 'undefined' ? `${screen.width}x${screen.height} @${window.devicePixelRatio}x` : undefined,
    Window: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : undefined,
  };
  const lines = [
    'MedicAI app logs',
    ...Object.entries(header).filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => `${k}: ${v}`),
    '',
    `${entries.length} entries, oldest first${entries.length >= MAX_ENTRIES ? ` (only the latest ${MAX_ENTRIES} are kept)` : ''}`,
    '',
    ...entries.map((e) => `${iso(e.at)} ${e.level.toUpperCase().padEnd(9)} ${e.text}`),
  ];
  return `${lines.join('\n').replace(/\u0000/g, '')}\n`;
}
